import React, { useState, useMemo } from "react";
import { LuCalendar, LuBarChart3, LuRefreshCw } from "react-icons/lu";

import { EnhancedFilterState } from "../../pages/enhanced-dashboard";
import { Select } from "src/components/Form/select";
import Button from "src/components/Button/Button";
import { FaFilter, FaChartBar, FaExclamationTriangle, FaUsers } from "react-icons/fa";
import { languageTuples } from "src/utils/util";
import { UserLangCode } from "src/utils/util.types";
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend 
} from "recharts";
import { useTrendData } from "../../hooks/useTrendData";
import { TrendChartLoading } from "./trend-chart-loading";
import { TrendDataFilters } from "../../services/trend-data.service";

type Props = {
  filters: EnhancedFilterState;
};

const chartTypeOptions = [
  { value: "line", label: "Line Chart" },
  { value: "area", label: "Area Chart" },
  { value: "bar", label: "Bar Chart" },
];

const timeGranularityOptions = [
  { value: "daily", label: "Daily (Last 30 Days)" },
  { value: "weekly", label: "Weekly (Last 12 Weeks)" },
  { value: "custom", label: "Custom Date Range" },
];

const sessionStateOptions = [
  { value: "ALL", label: "All Session States" },
  { value: "STANDBY", label: "Standby" },
  { value: "REJECTED", label: "Rejected" },
  { value: "MISSED", label: "Missed" },
  { value: "ACTIVE", label: "Active" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const sessionTypeOptions = [
  { value: "ALL", label: "All Session Types" },
  { value: "DIRECT", label: "Direct Sessions" },
  { value: "BOOKING", label: "Booked Sessions" },
  { value: "LOUNGE", label: "Lounge Sessions" },
];

const coachTypeOptions = [
  { value: "ALL", label: "All Coach Types" },
  { value: "PRO", label: "Professional Coaches" },
  { value: "VOLUNTEER", label: "Volunteer Coaches" },
];

export const EnhancedSessionsSection: React.FC<Props> = ({ filters }) => {
  const [sectionFilters, setSectionFilters] = useState({
    chartType: "line",
    timeGranularity: "daily",
    language: undefined as UserLangCode | undefined,
    sessionState: "ALL" as "ALL" | "STANDBY" | "REJECTED" | "MISSED" | "ACTIVE" | "COMPLETED" | "CANCELLED",
    sessionType: "ALL" as "ALL" | "DIRECT" | "BOOKING" | "LOUNGE",
    coachType: "ALL" as "ALL" | "PRO" | "VOLUNTEER",
    showFilters: false,
    startDate: undefined as Date | undefined,
    endDate: new Date() as Date | undefined, // Default to current day
  });

  // Convert section filters to API filters
  const apiFilters: TrendDataFilters = useMemo(() => ({
    timeGranularity: sectionFilters.timeGranularity as "daily" | "weekly" | "custom",
    metric: "sessions", // Fixed to sessions for this section
    role: "ALL",
    language: sectionFilters.language,
    sessionState: sectionFilters.sessionState,
    sessionType: sectionFilters.sessionType,
    coachType: sectionFilters.coachType,
    startDate: sectionFilters.startDate,
    endDate: sectionFilters.endDate,
  }), [sectionFilters.timeGranularity, sectionFilters.language, sectionFilters.sessionState, sectionFilters.sessionType, sectionFilters.coachType, sectionFilters.startDate, sectionFilters.endDate]);

  // Fetch real trend data using the custom hook
  const { data: trendData, loading, error, refetch, isInitialLoading } = useTrendData(apiFilters);

  const handleSectionFilterChange = (key: string, value: string | boolean) => {
    setSectionFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleFilters = () => {
    setSectionFilters(prev => ({ ...prev, showFilters: !prev.showFilters }));
  };

  const renderChart = () => {
    if (!trendData?.data) return null;
    
    const commonProps = {
      data: trendData.data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
    };

    const renderTooltip = (props: any) => {
      const { active, payload, label } = props;
      if (active && payload && payload.length) {
        return (
          <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
            <p className="font-medium text-gray-900">{label}</p>
            {payload.map((entry: any, index: number) => (
              <p key={index} style={{ color: entry.color }} className="text-sm">
                {entry.name}: {entry.value}
              </p>
            ))}
          </div>
        );
      }
      return null;
    };

    switch (sectionFilters.chartType) {
      case "line":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date"
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="#10b981" 
                strokeWidth={3}
                dot={{ fill: "#10b981", strokeWidth: 2, r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case "area":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date"
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="total" 
                stroke="#10b981" 
                fill="#10b981" 
                fillOpacity={0.3}
                strokeWidth={3}
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case "bar":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date"
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis stroke="#6b7280" fontSize={12} />
              <Tooltip content={renderTooltip} />
              <Legend />
              <Bar dataKey="total" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-6">
        <LuCalendar className="w-5 h-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">Session Activity Trends</h2>
      </div>
      
      <div className="flex items-center justify-end mb-6">
        <Button
          type="button"
          onClick={toggleFilters}
          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-sm font-medium shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-200 transform hover:scale-105"
        >
          <FaFilter className="text-sm" />
          {sectionFilters.showFilters ? "Hide" : "Show"} Filters
        </Button>
      </div>

      {/* Section-Specific Filters */}
      {sectionFilters.showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <FaChartBar className="text-gray-500" />
            Chart Configuration
          </h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <LuBarChart3 className="text-gray-500" />
                Chart Type
              </label>
              <Select
                className="w-full"
                options={chartTypeOptions}
                label=""
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                onChange={(val) => handleSectionFilterChange("chartType", String(val) || "line")}
              />
            </div>
            
                         <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <LuCalendar className="text-gray-500" />
                 Time Granularity
               </label>
               <Select
                 className="w-full"
                 options={timeGranularityOptions}
                 label=""
                 getOptionValue={(option) => option.value}
                 getOptionLabel={(option) => option.label}
                 onChange={(val) => handleSectionFilterChange("timeGranularity", String(val) || "daily")}
               />
             </div>
             
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <LuCalendar className="text-gray-500" />
                 Language
               </label>
               <Select
                 className="w-full"
                 options={languageTuples}
                 label=""
                 includeOption="All Languages"
                 getOptionValue={(tuple) => tuple[0]}
                 getOptionLabel={(tuple) => tuple[1]}
                 onChange={(val) => handleSectionFilterChange("language", val as UserLangCode)}
               />
             </div>
             
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <FaChartBar className="text-gray-500" />
                 Session State
               </label>
               <Select
                 className="w-full"
                 options={sessionStateOptions}
                 label=""
                 getOptionValue={(option) => option.value}
                 getOptionLabel={(option) => option.label}
                 onChange={(val) => handleSectionFilterChange("sessionState", String(val) || "ALL")}
               />
             </div>
             
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <FaUsers className="text-gray-500" />
                 Session Type
               </label>
               <Select
                 className="w-full"
                 options={sessionTypeOptions}
                 label=""
                 getOptionValue={(option) => option.value}
                 getOptionLabel={(option) => option.label}
                 onChange={(val) => handleSectionFilterChange("sessionType", String(val) || "ALL")}
               />
             </div>
             
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                 <FaUsers className="text-gray-500" />
                 Coach Type
               </label>
               <Select
                 className="w-full"
                 options={coachTypeOptions}
                 label=""
                 getOptionValue={(option) => option.value}
                 getOptionLabel={(option) => option.label}
                 onChange={(val) => handleSectionFilterChange("coachType", String(val) || "ALL")}
               />
             </div>
            
            {sectionFilters.timeGranularity === "custom" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <LuCalendar className="text-gray-500" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={sectionFilters.startDate ? sectionFilters.startDate.toISOString().split('T')[0] : ""}
                    max={sectionFilters.endDate ? sectionFilters.endDate.toISOString().split('T')[0] : undefined}
                    onChange={(e) => {
                      const startDate = e.target.value ? new Date(e.target.value) : undefined;
                      setSectionFilters(prev => ({ 
                        ...prev, 
                        startDate,
                        // Reset end date if start date is after current end date
                        endDate: prev.endDate && startDate && startDate > prev.endDate ? undefined : prev.endDate
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <LuCalendar className="text-gray-500" />
                    End Date
                  </label>
                  <input
                    type="date"
                    value={sectionFilters.endDate ? sectionFilters.endDate.toISOString().split('T')[0] : ""}
                    min={sectionFilters.startDate ? sectionFilters.startDate.toISOString().split('T')[0] : undefined}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      const endDate = e.target.value ? new Date(e.target.value) : undefined;
                      setSectionFilters(prev => ({ 
                        ...prev, 
                        endDate
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div className="col-span-full">
                  <div className="text-xs text-gray-500 bg-yellow-50 p-2 rounded border border-yellow-200">
                    <p>⚠️ <strong>Note:</strong> Large date ranges may take longer to load. For best performance, keep ranges under 90 days.</p>
                  </div>
                </div>
              </>
            )}
          </div>
          
                     <div className="mt-4 flex flex-wrap gap-2">
             <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
               Chart: {chartTypeOptions.find(c => c.value === sectionFilters.chartType)?.label}
             </span>
             <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
               Period: {timeGranularityOptions.find(t => t.value === sectionFilters.timeGranularity)?.label}
             </span>
             {sectionFilters.language && (
               <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                 Language: {languageTuples.find(t => t[0] === sectionFilters.language)?.[1] || sectionFilters.language}
               </span>
             )}
             {sectionFilters.sessionState && sectionFilters.sessionState !== "ALL" && (
               <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                 State: {sessionStateOptions.find(s => s.value === sectionFilters.sessionState)?.label}
               </span>
             )}
             {sectionFilters.sessionType && sectionFilters.sessionType !== "ALL" && (
               <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800">
                 Type: {sessionTypeOptions.find(t => t.value === sectionFilters.sessionType)?.label}
               </span>
             )}
             {sectionFilters.coachType && sectionFilters.coachType !== "ALL" && (
               <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                 Coach: {coachTypeOptions.find(c => c.value === sectionFilters.coachType)?.label}
               </span>
             )}
           </div>
        </div>
      )}

      {/* Chart Container */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="mb-4">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">
            Session Activity Trends
          </h4>
        </div>
        
        {/* Loading State */}
        {isInitialLoading && (
          <TrendChartLoading 
            chartType={sectionFilters.chartType as "line" | "area" | "bar"}
            timeGranularity={sectionFilters.timeGranularity}
            metric="sessions"
          />
        )}
        
        {/* Error State */}
        {error && !isInitialLoading && (
          <div className="h-96 flex items-center justify-center">
            <div className="text-center">
              <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Failed to Load Session Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button
                type="button"
                onClick={refetch}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
              >
                <LuRefreshCw className="text-sm" />
                Retry
              </Button>
            </div>
          </div>
        )}
        
        {/* Chart or Loading State */}
        {!isInitialLoading && !error && (
          loading ? (
            <TrendChartLoading 
              chartType={sectionFilters.chartType as "line" | "area" | "bar"}
              timeGranularity={sectionFilters.timeGranularity}
              metric="sessions"
            />
          ) : (
            renderChart()
          )
        )}
      </div>

    </div>
  );
};
